import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  IconButton,
  Grid,
} from "@material-ui/core";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "ra-core";

import { QrBarcodeScanner } from "./QrBarcodeScanner/QrBarcodeScanner";
import { beep } from "../../utils/utils";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    height: 38,
    width: 38,
  },
  content: {
    padding: theme.spacing(2),
  },
}));

const defaultValuesMatch = (expected, entered) => expected === entered;

export const IdentifierVerificationDialog = ({
  open,
  title,
  subtitle,
  fieldLabel,
  expectedIdentifier,
  valuesMatch = defaultValuesMatch,
  successNotifyMessage,
  mismatchHelperText,
  validateAriaLabel,
  inputAriaLabel = null,
  allowExceptionOnMismatch = false,
  onProceedWithException,
  scanEnabled = false,
  mismatchWarningNotify = null,
  onClose,
  onValidationSuccess = () => {},
}) => {
  const styles = useStyles();
  const notify = useNotify();
  const inputRef = useRef(null);
  const mountedRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const [enteredValue, setEnteredValue] = useState("");
  const [error, setError] = useState(false);
  const [showMismatchActions, setShowMismatchActions] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setEnteredValue("");
    setError(false);
    setShowMismatchActions(false);
    setSubmitting(false);
    submitInFlightRef.current = false;

    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [open, expectedIdentifier]);

  const runSubmittingAction = async (action) => {
    if (submitInFlightRef.current) return;

    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      await Promise.resolve(action());
    } finally {
      submitInFlightRef.current = false;
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  const validate = () => {
    if (submitInFlightRef.current) return;

    const trimmed = enteredValue.trim();
    if (!trimmed) return;

    if (valuesMatch(expectedIdentifier, trimmed)) {
      if (successNotifyMessage) {
        notify(successNotifyMessage);
      }
      runSubmittingAction(onValidationSuccess);
      return;
    }

    setError(true);
    if (mismatchWarningNotify) {
      notify(mismatchWarningNotify, "warning");
    }
    if (allowExceptionOnMismatch && onProceedWithException) {
      setShowMismatchActions(true);
    }
  };

  const handleInputChange = (evt) => {
    setEnteredValue(evt.target.value);
    setError(false);
    setShowMismatchActions(false);
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    if (enteredValue.trim()) {
      validate();
    }
  };

  const handleScanSuccess = (value) => {
    if (enteredValue !== value) {
      beep(100, 520, 200);
      setEnteredValue(value);
      setError(false);
      setShowMismatchActions(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={submitting ? undefined : onClose}
    >
      <Box
        component="div"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        m={2}
      >
        <Typography variant="h6">{title}</Typography>
        <IconButton
          className={styles.iconButton}
          aria-label="Close dialog"
          onClick={onClose}
          disabled={submitting}
        >
          <CancelOutlinedIcon fontSize="large" />
        </IconButton>
      </Box>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent className={styles.content}>
          <Grid container spacing={2}>
            {subtitle ? (
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  {subtitle}
                </Typography>
              </Grid>
            ) : null}
            {scanEnabled && (
              <Grid item xs={12}>
                <QrBarcodeScanner onScanSuccess={handleScanSuccess} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                inputRef={inputRef}
                label={fieldLabel}
                variant="outlined"
                margin="dense"
                fullWidth
                onChange={handleInputChange}
                value={enteredValue}
                error={error}
                helperText={error ? mismatchHelperText : ""}
                aria-label={
                  inputAriaLabel || `Enter ${fieldLabel.toLowerCase()}`
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          {!showMismatchActions ? (
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={!enteredValue.trim() || submitting}
              aria-label={validateAriaLabel}
            >
              Validate
            </Button>
          ) : (
            <>
              <Button
                type="button"
                onClick={() => {
                  setShowMismatchActions(false);
                  setError(false);
                }}
                aria-label="Try again after mismatch"
              >
                Try again
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={() => runSubmittingAction(onProceedWithException)}
                aria-label="Proceed with Exception"
                disabled={submitting}
              >
                Proceed with Exception
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};
